import { Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { ofType, StateObservable } from 'redux-observable'
import { GlobalState } from 'store'
import { RepositoryActionTypes, RepositoryActionUpdateField } from 'store/repository'
import { CollateralBasketType, EntityType, HaircutType, OrderActions, OrderTerm, RepoType, SettlementVenue } from 'api'
import { applyUpdatesForWindow, isTriParty, resetNegotiationInstruction } from 'model'
import { OrderFieldNames } from 'view/routes/staging/StagingOrdersGrid/StagingGridColumnNames'
import { sliceFieldName } from 'utils'
import { createIsInstrumentCollateralBasketSelector } from 'selectors/instrumentSelectors'
import { isNil } from 'lodash-es'
import { defaultPrimeBrokerIdGetterSelector, tradingEntityMetaByAliasGetterSelector } from 'selectors/tradingEntitySelectors'
import { tradingRelationshipGetterSelector } from 'selectors/tradingRelationshipSelectors'
import { BTQAction } from 'epics'
import { getProductSectorFromProductSectorId } from 'selectors/productSelectors'

export const OrderStartDateFieldName = sliceFieldName(OrderFieldNames.START_DATE, 1)
export const OrderEndDateFieldName = sliceFieldName(OrderFieldNames.END_DATE, 1)
export const OrderIsinFieldName = sliceFieldName(OrderFieldNames.ISIN, 1)
export const OrderBasketIdFieldName = sliceFieldName(OrderFieldNames.BASKET_ID, 1)
export const OrderNotionalFieldName = sliceFieldName(OrderFieldNames.NOTIONAL, 1)
export const OrderStartConsiderationFieldName = sliceFieldName(OrderFieldNames.START_CONSIDERATION, 1)
export const OrderCallableTypeFieldName = sliceFieldName(OrderFieldNames.CALLABLE_TYPE, 1)
export const OrderCurrencyFieldName = sliceFieldName(OrderFieldNames.CURRENCY, 1)
export const OrderFundIdFieldName = sliceFieldName(OrderFieldNames.FUND_ID, 1)
export const OrderCounterpartyNameFieldName = sliceFieldName(OrderFieldNames.COUNTERPARTY_NAME, 1)
export const OrderAliasFieldName = sliceFieldName(OrderFieldNames.ALIAS, 1)
export const OrderPrimeBrokerFieldName = sliceFieldName(OrderFieldNames.PRIME_BROKER, 1)
export const OrderMasterLegalAgreementTypeFieldName = sliceFieldName(OrderFieldNames.MASTER_LEGAL_AGREEMENT_TYPE, 1)
export const OrderRepoTypeFieldName = sliceFieldName(OrderFieldNames.REPO_TYPE, 1)
export const OrderHaircutFieldName = sliceFieldName(OrderFieldNames.HAIRCUT, 1)
export const OrderHaircutTypeFieldName = sliceFieldName(OrderFieldNames.HAIRCUT_TYPE, 1)
export const OrderStartConsiderationOverride = sliceFieldName(OrderFieldNames.START_CONSIDERATION_OVERRIDE, 1)

export function orderModifyEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, RepositoryActionTypes.REPOSITORY_UPDATE_FIELD, RepositoryActionUpdateField>(
      RepositoryActionTypes.REPOSITORY_UPDATE_FIELD
    ),
    filter(action => action.payload.entityType === EntityType.ORDER),
    map(action => {
      const entry = state$.value.repositoryStore.ORDER[action.payload.entityId]
      let order = applyUpdatesForWindow(entry, action.meta.sourceWindowId)

      const getDefaultPrimeBrokerId = defaultPrimeBrokerIdGetterSelector(state$.value)
      const getTradingRelationship = tradingRelationshipGetterSelector(state$.value)
      const getMetaByAlias = tradingEntityMetaByAliasGetterSelector(state$.value)
      const getProductSector = getProductSectorFromProductSectorId(state$.value)

      const instrumentId = order.collateralId ?? order.basketAllocation?.basketId ?? null
      const productSector = getProductSector(order.productSectorId)
      const isGcPlusBasket = productSector?.basketType === CollateralBasketType.GC_PLUS
      const isTripartyBasket = productSector?.basketType ? isTriParty(productSector.basketType) : false
      const newSettlementVenue = isGcPlusBasket
        ? null
        : isTripartyBasket
        ? order.negotiationInstruction.settlementVenue
        : SettlementVenue.Bilateral
      let collateralId = order.collateralId
      let basketId = order.basketAllocation?.basketId ?? null

      if (action.payload.fieldName === OrderStartDateFieldName || action.payload.fieldName === OrderEndDateFieldName) {
        order.term = isNil(order.endDate) ? OrderTerm.CUSTOM_O : OrderTerm.CUSTOM
      } else if (action.payload.fieldName === OrderNotionalFieldName) {
        order.startConsideration = null
      } else if (action.payload.fieldName === OrderStartConsiderationFieldName) {
        order.notional = null
      } else if (action.payload.fieldName === OrderIsinFieldName || action.payload.fieldName === OrderBasketIdFieldName) {
        const isBasket = createIsInstrumentCollateralBasketSelector(() => instrumentId)(state$.value, {})
        // BQ-3731 currency set to null when collateral changes in case user has entered in value
        order.currency = null
        order.haircut = null
        order.haircutType = null
        if (isBasket) {
          basketId = instrumentId
          collateralId = null
        } else {
          collateralId = instrumentId
          basketId = null
        }
      } else if (action.payload.fieldName === OrderFundIdFieldName) {
        order.blockTrade = null
        order = resetNegotiationInstruction(order, newSettlementVenue)
        order.negotiationInstruction.counterpartyInstructions = []
      } else if (action.payload.fieldName === OrderCounterpartyNameFieldName || action.payload.fieldName === OrderAliasFieldName) {
        order = resetNegotiationInstruction(order, newSettlementVenue)
        if (action.payload.fieldValue === null) {
          order.negotiationInstruction.counterpartyInstructions = []
          order.masterLegalAgreementType = null
        } else {
          if (
            order.negotiationInstruction.counterpartyInstructions.length === 1 &&
            action.payload.fieldName === OrderCounterpartyNameFieldName
          ) {
            order.negotiationInstruction.counterpartyInstructions[0].counterpartyAlias = null
          } else if (
            order.negotiationInstruction.counterpartyInstructions.length === 1 &&
            action.payload.fieldName === OrderAliasFieldName
          ) {
            const alias = order.negotiationInstruction.counterpartyInstructions[0].counterpartyAlias

            if (alias && order.fundId) {
              const metaFound = getMetaByAlias(alias, order.fundId)

              if (metaFound) {
                order.negotiationInstruction.counterpartyInstructions[0].counterpartyId = metaFound?.subjectTradingEntityId
              }
            }
          }

          if (order.fundId) {
            order.negotiationInstruction.counterpartyInstructions[0].primeBrokerId =
              getDefaultPrimeBrokerId(order.fundId, order.negotiationInstruction.counterpartyInstructions[0].counterpartyId) ??
              order.primeBrokerId
          }

          const tradingRelationship = order.fundId && getTradingRelationship(order.fundId, action.payload.fieldValue as string)

          if (tradingRelationship) {
            if (tradingRelationship.defaultAgreement) {
              order.masterLegalAgreementType = tradingRelationship.defaultAgreement
            } else {
              order.masterLegalAgreementType = null
            }
          }
        }
      } else if (action.payload.fieldName === OrderPrimeBrokerFieldName) {
        order.negotiationInstruction.counterpartyInstructions = order.negotiationInstruction.counterpartyInstructions.map(
          counterpartyInstruction => ({ ...counterpartyInstruction, primeBrokerId: order.primeBrokerId })
        )
      } else if (action.payload.fieldName === OrderMasterLegalAgreementTypeFieldName) {
        order.negotiationInstruction.counterpartyInstructions = order.negotiationInstruction.counterpartyInstructions.map(
          counterpartyInstruction => ({ ...counterpartyInstruction, masterLegalAgreementType: null })
        )
      } else if (action.payload.fieldName === OrderRepoTypeFieldName) {
        order = resetNegotiationInstruction(order, newSettlementVenue)
        order.negotiationInstruction.counterpartyInstructions = []
        if (action.payload.fieldValue === RepoType.STOCK_BORROWING_LENDING) {
          order.rate = null
        }
        if (action.payload.fieldValue === RepoType.BSB || action.payload.fieldValue === RepoType.CLASSIC) {
          order.fee = null
        }
      } else if (action.payload.fieldName === OrderStartConsiderationOverride && order.startConsiderationOverride === true) {
        order.startConsideration = null
        order.notional = null
      } else if (action.payload.fieldName === OrderHaircutFieldName && order.haircutType === null && order.haircut !== null) {
        order.haircutType = HaircutType.HAIRCUT
      } else if (action.payload.fieldName === OrderHaircutFieldName && order.haircut === null) {
        order.haircutType = null
      }

      return OrderActions.modify(
        {
          orderId: order.orderId,
          externalOrderId: order.externalOrderId,
          positionId: order.positionId,
          collateralId: collateralId,
          currency: order.currency,
          basketId: basketId,
          fundId: order.fundId,
          fundShortName: null,
          referenceRate: order.referenceRate,
          direction: order.direction,
          notional: order.notional,
          rate: order.rate,
          bondPrice: order.bondPrice,
          startConsideration: order.startConsideration,
          startDate: order.startDate,
          startDateTerm: null,
          endDate: order.endDate,
          endDateTerm: null,
          endDateTerms: null,
          endDates: null,
          parentOrderId: order.parentOrderId,
          term: order.term,
          book: order.book,
          strategy: order.strategy,
          bundle: order.bundle,
          primeBrokerId: order.primeBrokerId,
          repoType: order.repoType,
          investmentDecisionMaker: order.investmentDecisionMaker,
          executionDecisionMaker: order.executionDecisionMaker,
          negotiationInstruction: order.negotiationInstruction,
          callableType: order.callableType,
          callablePeriodDays: order.callablePeriodDays,
          noticePeriodDays: order.noticePeriodDays,
          csd: null,
          backToStagingTradingType: null,
          blockTrade: !!order?.nestedLegs?.legs ? false : order.blockTrade,
          rfqType: order.rfqType,
          notes: order.notes,
          uploadRowIndex: order.uploadRowIndex,
          allowPartial: order.allowPartial,
          targetFillNotional: order.targetFillNotional,
          targetFillStartConsideration: order.targetFillStartConsideration,
          preAgreed: order.preAgreed,
          masterLegalAgreementType: order.masterLegalAgreementType,
          settlementVenue: null,
          clearingHouseId: null,
          sponsorId: null,
          startConsiderationOverride: order.startConsiderationOverride,
          fee: order.fee,
          secondaryBasketId: order.secondaryBasketId,
          secondaryCollateralId: order.secondaryCollateralId,
          stockBorrowingLendingType: order.stockBorrowingLendingType,
          evergreenNoticePeriod: order.evergreenNoticePeriod,
          dateSource: order.dateSource,
          rightOfExit: order.rightOfExit ? 1 : 0,
          rightOfSubstitution: order.rightOfSubstitution ? 1 : 0,
          haircutType: order.haircutType,
          haircut: order.haircut
        },
        action.meta.sourceWindowId,
        action.payload.correlationId
      )
    })
  )
}
